import { HttpErrorResponse } from '@angular/common/http'
import { Component, OnInit } from '@angular/core'
import { FormGroup, FormBuilder, Validators } from '@angular/forms'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { CategorySelection, QueriesAPIModel, TaskTypeAPIModel } from 'src/app/models/api.model'
import { CommonService, getNextWorkingDay } from 'src/app/services/common/common.service'
import { DocumentService } from 'src/app/services/common/document.service'
import { InitializeService } from 'src/app/services/initialize/initialize.service'
import { Observable, startWith, map, of } from 'rxjs'
import { QueryService } from 'src/app/services/query/query.service'

@Component({
  selector: 'app-create-query',
  templateUrl: './create-query.component.html',
  styleUrls: ['./create-query.component.scss']
})
export class CreateQueryComponent implements OnInit {
  createQueryForm: FormGroup
  filesToBeUploaded: FileList

  queryTaskTypes: TaskTypeAPIModel[] = []
  uploadedDocuments: any[]

  readonly titleControlName = 'title'
  selectTaskTypesSubscription$: any

  filteredTaskTypes: Observable<TaskTypeAPIModel[]>
  otherQueryTask: TaskTypeAPIModel

  private readonly OTHER_QUERY = 'OTHER_QUERY'
  private readonly TASK_CATEGORY = 'taskCategory'
  private readonly TASK_SUB_CATEGORY = 'taskSubCategory'
  readonly module = GlobalConstants.MODULE.QUERIES
  readonly defaultPriority = 'medium'


  constructor(
    private formBuilder: FormBuilder,
    private queryService: QueryService,
    private cs: CommonService,
    private documentService: DocumentService,
    private initializeService: InitializeService
  ) {
  }

  ngOnInit(): void {
    this.buildCreateQueryForm()
    this.filteredTaskTypes = this.createQueryForm.get(this.titleControlName).valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || ''))
    )
  }

  buildCreateQueryForm() {
    this.createQueryForm = this.formBuilder.group({
      [this.titleControlName]: ['', [Validators.required]],
      description: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(5000)]],
      [this.TASK_CATEGORY]: ['', [Validators.required]],
      [this.TASK_SUB_CATEGORY]: ['', [Validators.required]]
    })
  }

  saveAndExit() {
    if (this.createQueryForm.invalid) {
      this.createQueryForm.markAllAsTouched()
      return
    }
    const selectedTaskType =
      this.queryTaskTypes.find((taskType) => taskType.label === this.createQueryForm.value[this.titleControlName]) ||
      this.otherQueryTask

    const status = selectedTaskType?.statusList.find((status) => status.defaultStatus)?.code
    const nextAction = selectedTaskType?.statusList.find((status) => status.defaultStatus)?.nextActionToBeTaken
    const taskCode = selectedTaskType?.code

    if (!status) {
      return this.cs.showError('Query Status is required')
    }

    if (!taskCode) {
      return this.cs.showError('Task Code not found')
    }

    const query = {
      ...this.createQueryForm.value,
      documents: this.uploadedDocuments,
      responseDueDatetime: getNextWorkingDay(),
      status,
      taskCode,
      priority: this.defaultPriority,
      nextAction: nextAction
    }

    this.dispatchSaveQueryAction(query, this.filesToBeUploaded)
  }

  private dispatchSaveQueryAction(query: QueriesAPIModel, files: FileList) {
    this.queryService.dispatchSaveQueryAction(
      query,
      files,
      this.cs.createRoutingURL([GlobalConstants.ROUTING.QUERY, GlobalConstants.ROUTING.DASHBOARD])
    )
  }

  exit() {
    this.cs.route(GlobalConstants.ROUTING.QUERY, GlobalConstants.ROUTING.DASHBOARD)
  }

  upload() {
    this.documentService
      .uploadDocuments(GlobalConstants.MODULE.QUERIES, GlobalConstants.DOCUMENT_TYPE.QUERY_DOC, this.filesToBeUploaded)
      .subscribe({
        next: (responses) => {
          this.uploadedDocuments = responses
        },
        error: (error: HttpErrorResponse) => {
          this.cs.showError(error.message)
        },
        complete: () => {
          this.filesToBeUploaded = null
          this.cs.showSuccess('Successfully Uploaded')
        }
      })
  }

  handleFileInput(files: FileList) {
    this.filesToBeUploaded = files
  }

  clearSelectedFiles(flag: boolean) {
    if (flag) {
      this.filesToBeUploaded = null
    }
  }

  get f() {
    return this.createQueryForm.controls
  }

  private getQueryTaskTypes(categorySelection: CategorySelection) {
    const { selectedCategory, selectedSubCategory } = categorySelection

    this.otherQueryTask = null
    this.queryTaskTypes = []
    this.filteredTaskTypes = null
    this.selectTaskTypesSubscription$ = this.initializeService
      .getFilteredTaskTypes(GlobalConstants.MODULE.QUERIES, selectedCategory, selectedSubCategory)
      .subscribe((taskTypes: TaskTypeAPIModel[]) => {
        if (taskTypes) {
          this.queryTaskTypes = [...taskTypes]
          this.otherQueryTask = this.queryTaskTypes.filter((taskType) =>
            taskType.code.startsWith(this.OTHER_QUERY)
          )?.[0]
          const taskTypesToShow = this.queryTaskTypes.filter((taskType) => !taskType.code.startsWith(this.OTHER_QUERY))
          this.filteredTaskTypes = of(taskTypesToShow)
        }
      })
  }

  onCategorySelected(categorySelection: CategorySelection) {
    const { selectedCategory, selectedSubCategory } = categorySelection
    this.createQueryForm.get(this.titleControlName).markAsUntouched()
    this.createQueryForm.get(this.titleControlName).setValue('')
    this.createQueryForm.get(this.TASK_CATEGORY).setValue(selectedCategory)
    this.createQueryForm.get(this.TASK_SUB_CATEGORY).setValue(selectedSubCategory)
    this.queryTaskTypes = []
    this.getQueryTaskTypes(categorySelection)
  }

  ngOnDestroy() {
    this.selectTaskTypesSubscription$?.unsubscribe()
  }

  private _filter(value: string): TaskTypeAPIModel[] {
    const filterValue = this._normalizeValue(value)
    return this.queryTaskTypes.filter((queryTaskType) =>
      this._normalizeValue(queryTaskType.label).includes(filterValue)
    )
  }

  private _normalizeValue(value: string): string {
    return value.toLowerCase().replace(/\s/g, '')
  }
}
